// One store (02-DESIGN/10-state/01-stores-types-and-instances.md § the console:
// Stores; 03-indexes.md § the search kind, § the query surface;
// 01-the-console.md § the words: *an item* for an instance, *a change* for
// an operation, *current* for the folded state, *type*, *index*, *search*,
// *history*; § conventions: tabs, a table). The header names the store — its
// description, history policy, size and fill — and the page holds it in two
// tabs, so that working with what is in a store is apart from changing what
// the store is:
//
// - *Contents*, the one the page opens on: every item of the store in the
//   one table — path, type, last change — a page at a time with the count of
//   all of them, the table's filter, sort and a *Type* facet over what is
//   read; a row opens the item's page, `stores/<store>/items/<path>`, with
//   its current state and its history (ItemPage.tsx); *Open by path* stays,
//   secondary, for a path already known. Above the list, the search of
//   the store's search index — the words, nothing more — answered as items by
//   path, best first, with the count of everything that matched; *rebuilding*
//   while the index catches up; a hit opens the item from the store, never
//   from the index. Contents writes nothing.
// - *Settings*: the store's own settings — size, when full, copies, history —
//   with *Change settings* (§ size, when full, and copies; StoreSettings.tsx);
//   the types, each a row opening its page, `stores/<store>/types/<type>`,
//   where it is read and revised (TypePage.tsx); the indexes, each a row
//   opening its page, `stores/<store>/indexes/<index>` (IndexPage.tsx),
//   declared and deleted, never edited. *Change settings*, *Define a type*
//   and *Declare an index* are forms in the tab's place (01-the-console.md
//   § conventions: a form), and the new row is marked when one closes.
//
// *Delete the store* is the last entry of the page's menu, asked first
// (01-the-console.md § conventions: remove; the moves, page by page).
//
// Creating a store stays on the stores list.

import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import {
  Button,
  bytes,
  Choices,
  Empty,
  Field,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  Select,
  since,
  TextInput,
  useLoad,
  when,
} from "../../ui/components.tsx";
import { RowMenu, Table, type Column } from "../../ui/table.tsx";
import { Tabs, useTab, type TabSpec } from "../../ui/tabs.tsx";
import {
  Code,
  HistoryPolicy,
  IndexKind,
  IndexSource,
  validName,
  validPath,
  validStoreName,
  type Hit,
  type IndexDeclaration,
  type Item as ListedItem,
  type NamedIndex,
  type NamedType,
  type SearchResult,
  type Store,
} from "../../wire/state.ts";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { useSurface } from "../surface.tsx";
import {
  indexAddress,
  itemAddress,
  SETTINGS_TAB,
  storeFormTab,
  tabOf,
  typeAddress,
  type StoreForm,
} from "./addresses.ts";
import { searchIndexes } from "./contents.ts";
import { DefineType } from "./DefineType.tsx";
import { ChangeSettings, StoreSettings } from "./StoreSettings.tsx";

export function StorePage() {
  const { store = "" } = useParams();
  const s = useSurface();
  if (!validStoreName(store)) {
    return (
      <PageHead
        crumb={<Link to={tenantPath(s.tenant, "stores")}>Stores</Link>}
        title="Not a store"
        line={`"${store}" is not a store's name.`}
      />
    );
  }
  return <One store={store} />;
}

type Tab = "contents" | typeof SETTINGS_TAB;

/** Marked is the row a closing form added, marked in its table for a moment, and what the tab says of it. */
interface Marked {
  type?: string;
  index?: string;
  note?: string;
}

const TABS: readonly TabSpec<Tab>[] = [
  { id: "contents", label: "Contents" },
  { id: "settings", label: "Settings" },
];

function One({ store }: { store: string }) {
  const s = useSurface();
  const [load, reload] = useLoad(`store/${store}`, () => s.bus.stores().getStore(store), 0, { keep: true });
  const location = useLocation();
  // A type's or an index's crumb comes back to the Settings tab, named in its link's state.
  const form = useFormState<StoreForm>();
  const [chosen, setTab] = useTab(TABS, tabOf(location.state) ?? storeFormTab(form.open));
  // Every form is the Settings tab's: while one is open — after a refresh, or Back and
  // Forward — that tab shows, and stays chosen when the form closes.
  const wanted = storeFormTab(form.open);
  if (wanted !== undefined && wanted !== chosen) {
    setTab(wanted);
  }
  const tab = wanted ?? chosen;
  const [marked, setMarked] = useState<Marked>({});
  const navigate = useNavigate();
  const [err, setErr] = useState<unknown>(undefined);
  const [removing, setRemoving] = useState(false);
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "stores")}>Stores</Link>
      <span>/</span>
      <span className="mono">{store}</span>
    </>
  );
  if (load.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the store" />
      </>
    );
  }
  if (load.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={store} />
        <NotAllowed err={load.err} what="This store" />
      </>
    );
  }
  const st = load.value;
  const remove = () => {
    setErr(undefined);
    setRemoving(true);
    s.bus
      .stores()
      .deleteStore(store)
      .then(
        () => {
          void navigate(tenantPath(s.tenant, "stores"));
        },
        (e: unknown) => {
          setRemoving(false);
          setErr(e);
        },
      );
  };
  return (
    <>
      <PageHead
        crumb={crumb}
        title={st.name}
        action={
          removing || form.open !== undefined ? undefined : (
            <RowMenu
              label={`Actions for ${st.name}`}
              entries={[
                {
                  label: "Delete the store",
                  danger: true,
                  onSelect: remove,
                  confirm: {
                    title: `Delete the store ${st.name}?`,
                    consequence: `For good: every item in ${st.name} and its whole history are gone, and nothing can bring them back.`,
                    verb: "Delete",
                  },
                },
              ]}
            />
          )
        }
        line={
          <>
            {st.description !== undefined && st.description !== "" ? `${st.description} · ` : ""}
            {st.history === HistoryPolicy.full ? "history kept whole" : "history compactable"} ·{" "}
            {bytes(st.fill)} of {bytes(st.budget)} used
          </>
        }
      />
      {err !== undefined && <NotAllowed err={err} />}
      {removing && <Pending what="Deleting the store" />}
      <Tabs label={st.name} tabs={TABS} value={tab} onChange={setTab}>
        {tab === "contents" ? (
          <Contents
            store={store}
            st={st}
            toSettings={() => {
              setTab("settings");
            }}
          />
        ) : form.open === "settings" ? (
          <ChangeSettings
            st={st}
            onClose={form.close}
            onChanged={() => {
              form.close();
              setMarked({ note: `Saved ${st.name}'s settings.` });
              reload();
            }}
          />
        ) : form.open === "define-type" ? (
          <DefineTypeForm
            store={store}
            onClose={form.close}
            onDefined={(name) => {
              form.close();
              // The reply's revision is the store's, not the type's: it is not said (hq issue 177).
              setMarked({
                type: name,
                note: `Defined ${name}. The store's current state is worked out again from its history under it.`,
              });
            }}
          />
        ) : form.open === "declare-index" ? (
          <DeclareIndex
            store={store}
            onClose={form.close}
            onDeclared={(name) => {
              form.close();
              setMarked({ index: name, note: `Declared ${name}. It is built from the history now.` });
            }}
          />
        ) : (
          <>
            {marked.note !== undefined && (
              <Notice kind="ok">
                {marked.note}{" "}
                <button
                  type="button"
                  className="text-link"
                  onClick={() => {
                    setMarked({ type: marked.type, index: marked.index });
                  }}
                >
                  Dismiss
                </button>
              </Notice>
            )}
            <StoreSettings
              st={st}
              onChange={() => {
                form.show("settings");
              }}
            />
            <Types
              store={store}
              mark={marked.type}
              onDefine={() => {
                form.show("define-type");
              }}
            />
            <Indexes
              store={store}
              mark={marked.index}
              onDeclare={() => {
                form.show("declare-index");
              }}
            />
          </>
        )}
      </Tabs>
    </>
  );
}

// ---- Contents --------------------------------------------------------------

/** Contents is every item of the store in one table, opened from its row or by its path, and searched through its search index. */
function Contents({ store, st, toSettings }: { store: string; st: Store; toSettings: () => void }) {
  const s = useSurface();
  const navigate = useNavigate();
  const [indexes] = useLoad(`store/${store}/indexes/search`, () => s.bus.stores().listIndexes(store));
  const to = (path: string) => itemAddress(s.tenant, store, path);
  const [found, setFound] = useState<{ words: string; result: SearchResult } | undefined>(undefined);
  const searchable = indexes.state === "ready" ? searchIndexes(indexes.value) : [];
  return (
    <>
      {indexes.state === "ready" && searchable.length > 0 && (
        <Search
          store={store}
          indexes={searchable}
          onFound={(words, result) => {
            setFound(words === "" ? undefined : { words, result });
          }}
        />
      )}
      {indexes.state === "ready" && searchable.length === 0 && (
        <p className="small muted">
          {st.name} has no search index, so its items are browsed only.{" "}
          <button type="button" className="text-link" onClick={toSettings}>
            Declare one under Settings
          </button>{" "}
          to search them.
        </p>
      )}
      {found !== undefined ? (
        <section className="section">
          <div className="section-head">
            <h2>
              {found.result.total === 1 ? "1 item matches" : `${found.result.total} items match`} &ldquo;
              {found.words}&rdquo;
            </h2>
            <Button
              size="sm"
              kind="quiet"
              onClick={() => {
                setFound(undefined);
              }}
            >
              Clear search
            </Button>
          </div>
          {found.result.hits.length === 0 ? (
            <Empty>Nothing in {st.name} matches those words.</Empty>
          ) : (
            <Table
              label="Matching items"
              rows={found.result.hits}
              rowKey={(h) => h.instance}
              sort={{ key: "score", dir: "desc" }}
              columns={hitColumns}
              to={(h) => to(h.instance)}
            />
          )}
        </section>
      ) : (
        <AllItems
          key={store}
          store={store}
          storeName={st.name}
          to={to}
          onOpen={(path) => {
            void navigate(to(path));
          }}
        />
      )}
    </>
  );
}

/** Search is the store's search: one field whose words are the query, answered as items by path, best first. */
function Search({
  store,
  indexes,
  onFound,
}: {
  store: string;
  indexes: readonly NamedIndex[];
  onFound: (words: string, result: SearchResult) => void;
}) {
  const s = useSurface();
  const [index, setIndex] = useState(indexes[0]?.name ?? "");
  const [words, setWords] = useState("");
  const [busy, setBusy] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const submit = () => {
    const q = words.trim();
    if (busy || index === "") {
      return;
    }
    setErr(undefined);
    setRebuilding(false);
    if (q === "") {
      onFound("", { hits: [], total: 0 });
      return;
    }
    setBusy(true);
    s.bus
      .stores()
      .search(store, index, { query: q })
      .then(
        (r) => {
          setBusy(false);
          onFound(q, r);
        },
        (e: unknown) => {
          setBusy(false);
          if (isRefusal(e, Code.indexNotReady)) {
            setRebuilding(true);
          } else {
            setErr(e);
          }
        },
      );
  };
  return (
    <section className="section">
      <form
        className="row"
        style={{ gap: 8, alignItems: "center" }}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <span className="grow">
          <TextInput value={words} onChange={setWords} placeholder="Search the items" />
        </span>
        {indexes.length > 1 && (
          <span style={{ width: "12rem" }}>
            <Select
              value={index}
              onChange={setIndex}
              options={indexes.map((ix) => ({ value: ix.name, label: ix.name }))}
            />
          </span>
        )}
        <Button kind="quiet" type="submit" disabled={busy}>
          {busy ? "Searching…" : "Search"}
        </Button>
      </form>
      {rebuilding && (
        <Notice title="Rebuilding">
          The index is catching up with the store, so it searches nothing yet. Search again in a moment.
        </Notice>
      )}
      {err !== undefined && <NotAllowed err={err} what="Search" />}
    </section>
  );
}

/** OpenByPath opens an item by a path the person already knows: the secondary way in, beside the list. */
function OpenByPath({ onOpen }: { onOpen: (path: string) => void }) {
  const [path, setPath] = useState("");
  const p = path.trim();
  const ok = validPath(p);
  return (
    <form
      className="row"
      style={{ gap: 8, alignItems: "center", marginBottom: 12 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (ok) {
          onOpen(p);
        }
      }}
    >
      <span className="grow">
        <TextInput
          value={path}
          onChange={setPath}
          placeholder="Open an item by its path: invoice/inv-1"
          mono
        />
      </span>
      <Button kind="quiet" type="submit" disabled={!ok}>
        Open
      </Button>
    </form>
  );
}

const hitColumns: Column<Hit>[] = [
  {
    key: "item",
    header: "Item",
    width: "minmax(0, 2fr)",
    value: (h) => h.instance,
    cell: (h) => <span className="cell-main mono">{h.instance}</span>,
  },
  {
    key: "score",
    header: "Score",
    width: "100px",
    align: "end",
    value: (h) => h.score,
    cell: (h) => <span className="muted">{h.score.toFixed(2)}</span>,
  },
];

export const itemColumns: Column<ListedItem>[] = [
  {
    key: "item",
    header: "Item",
    width: "minmax(0, 2fr)",
    value: (it) => it.instance,
    cell: (it) => <span className="cell-main mono">{it.instance}</span>,
  },
  {
    key: "type",
    header: "Type",
    width: "minmax(0, 1fr)",
    facet: "Type",
    value: (it) => it.type ?? "",
    facetLabel: (v) => (v === "" ? "untyped" : v),
    text: (it) => it.type ?? "untyped",
    cell: (it) =>
      it.type === undefined ? <span className="small muted">untyped</span> : <span>{it.type}</span>,
  },
  {
    key: "changed",
    header: "Last change",
    width: "150px",
    value: (it) => it.at,
    text: (it) => since(it.at) || when(it.at),
    cell: (it) => <span className="small muted">{since(it.at) || when(it.at)}</span>,
  },
];

/** ITEMS_PAGE is how many items one read of the store-wide list asks for. */
const ITEMS_PAGE = 500;

type ItemsRead =
  | { state: "pending" }
  | { state: "refused"; err: unknown }
  | { state: "ready"; rows: ListedItem[]; total: number; next?: string; more: boolean; moreErr?: unknown };

/**
 * AllItems is every item of the store in the one table, read a page at a
 * time in path order: the count of all of them over the table, *Show more*
 * while items remain, and the table's filter, sort and Type facet over the
 * rows read so far. *Open by path* sits beside it as the secondary way in.
 */
function AllItems({
  store,
  storeName,
  to,
  onOpen,
}: {
  store: string;
  storeName: string;
  /** An item's page, by its path. */
  to: (path: string) => string;
  /** Opens an item by a path the person typed. */
  onOpen: (path: string) => void;
}) {
  const s = useSurface();
  const [read, setRead] = useState<ItemsRead>({ state: "pending" });
  const [round, setRound] = useState(0);
  const [byPath, setByPath] = useState(false);
  // The connection is stable; the surface object is new each render and must not drive a read.
  const bus = s.bus;
  useEffect(() => {
    let live = true;
    bus
      .stores()
      .listItems(store, { limit: ITEMS_PAGE })
      .then(
        (p) => {
          if (live) {
            setRead({ state: "ready", rows: p.items, total: p.total, next: p.next, more: false });
          }
        },
        (err: unknown) => {
          if (live) {
            setRead({ state: "refused", err });
          }
        },
      );
    return () => {
      live = false;
    };
  }, [bus, store, round]);
  const showMore = () => {
    if (read.state !== "ready" || read.next === undefined || read.more) {
      return;
    }
    const was = read;
    setRead({ ...was, more: true, moreErr: undefined });
    s.bus
      .stores()
      .listItems(store, { after: was.next, limit: ITEMS_PAGE })
      .then(
        (p) => {
          setRead({
            state: "ready",
            rows: [...was.rows, ...p.items],
            total: p.total,
            next: p.next,
            more: false,
          });
        },
        (err: unknown) => {
          setRead({ ...was, more: false, moreErr: err });
        },
      );
  };
  return (
    <section className="section">
      <div className="section-head">
        <h2>
          Items
          {read.state === "ready" && read.total > 0 && (
            <span className="small muted">
              {" "}
              &middot; {read.total === 1 ? "1 item" : `${read.total} items`}
            </span>
          )}
        </h2>
        <Button
          size="sm"
          kind="quiet"
          onClick={() => {
            setByPath(!byPath);
          }}
        >
          Open by path
        </Button>
      </div>
      {byPath && <OpenByPath onOpen={onOpen} />}
      {read.state === "pending" && <Pending what="Listing the items" />}
      {read.state === "refused" && <NotAllowed err={read.err} what="Items" />}
      {read.state === "ready" && read.rows.length === 0 && (
        <Empty>
          Nothing in {storeName} yet. A workload or a member writes the first change, and the item appears
          here.
        </Empty>
      )}
      {read.state === "ready" && read.rows.length > 0 && (
        <>
          <Table
            label="Items"
            rows={read.rows}
            rowKey={(it) => it.instance}
            sort={{ key: "item", dir: "asc" }}
            columns={itemColumns}
            to={(it) => to(it.instance)}
          />
          {read.moreErr !== undefined && <NotAllowed err={read.moreErr} what="More items" />}
          <div
            className="row"
            style={{ justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 8 }}
          >
            {read.next !== undefined && (
              <>
                <span className="small muted">
                  {read.rows.length} of {read.total} read
                </span>
                <Button size="sm" kind="quiet" onClick={showMore} disabled={read.more}>
                  {read.more ? "Reading…" : "Show more"}
                </Button>
              </>
            )}
            <Button
              size="sm"
              kind="quiet"
              onClick={() => {
                setRead({ state: "pending" });
                setRound(round + 1);
              }}
            >
              Reload
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

// ---- Settings --------------------------------------------------------------

const typeColumns: Column<NamedType>[] = [
  {
    key: "type",
    header: "Type",
    width: "minmax(0, 1fr)",
    value: (t) => t.name,
    cell: (t) => (
      <span className="cell">
        <span className="cell-main mono">{t.name}</span>
        {t.history === HistoryPolicy.full && <span className="cell-sub">history kept whole</span>}
      </span>
    ),
  },
  {
    key: "changes",
    header: "Changes",
    width: "minmax(0, 1.4fr)",
    narrow: false,
    // Counted first; the changes themselves are on the type's page.
    value: (t) => Object.keys(t.operations ?? {}).length,
    text: (t) => Object.keys(t.operations ?? {}).join(" "),
    cell: (t) => {
      const n = Object.keys(t.operations ?? {}).length;
      return (
        <span className="small muted">
          {n === 0 ? (
            <span className="faint">none declared</span>
          ) : (
            `${String(n)} ${n === 1 ? "change" : "changes"}`
          )}
        </span>
      );
    },
  },
  {
    key: "children",
    header: "May hold",
    width: "minmax(0, 1fr)",
    narrow: false,
    value: (t) => Object.keys(t.children ?? {}).join(", "),
    cell: (t) => (
      <span className="small muted mono">
        {Object.entries(t.children ?? {})
          .map(([k, v]) => `${k} (${v})`)
          .join(", ") || <span className="faint">nothing</span>}
      </span>
    ),
  },
  {
    key: "version",
    header: "Version",
    width: "90px",
    align: "end",
    value: (t) => t.revision ?? 0,
    cell: (t) => <span className="muted">{t.revision ?? "—"}</span>,
  },
];

/** Types is the store's types, each row opening its page; *Define a type* opens its form in the tab's place. */
function Types({ store, mark, onDefine }: { store: string; mark: string | undefined; onDefine: () => void }) {
  const s = useSurface();
  const [types] = useLoad(`store/${store}/types/${mark ?? ""}`, () => s.bus.stores().listTypes(store));
  const define = (
    <Button size="sm" onClick={onDefine}>
      <Plus strokeWidth={1.8} aria-hidden="true" />
      Define a type
    </Button>
  );
  return (
    <section className="section">
      <div className="section-head">
        <h2>Types</h2>
        {define}
      </div>
      {types.state === "pending" && <Pending what="Reading the types" />}
      {types.state === "refused" && <NotAllowed err={types.err} what="Types" />}
      {types.state === "ready" && types.value.length === 0 && (
        <Empty action={define}>
          No types yet. A type says what an item looks like, which changes it admits and what each does; an
          item of no type holds state only once a snapshot is written for it.
        </Empty>
      )}
      {types.state === "ready" && types.value.length > 0 && (
        <Table
          label="Types"
          rows={types.value}
          rowKey={(t) => t.name}
          sort={{ key: "type", dir: "asc" }}
          columns={typeColumns}
          to={(t) => typeAddress(s.tenant, store, t.name)}
          mark={mark}
        />
      )}
    </section>
  );
}

/** DefineTypeForm is *Define a type* in the Settings tab's place, with the store's types for what an item may hold. */
function DefineTypeForm({
  store,
  onClose,
  onDefined,
}: {
  store: string;
  onClose: () => void;
  onDefined: (name: string) => void;
}) {
  const s = useSurface();
  const [types] = useLoad(`store/${store}/types/define`, () => s.bus.stores().listTypes(store));
  return (
    <DefineType
      store={store}
      existing={undefined}
      types={types.state === "ready" ? types.value : []}
      heading="h2"
      back="Settings"
      onClose={onClose}
      onDefined={onDefined}
    />
  );
}

// ---- Indexes ---------------------------------------------------------------

/** INDEX_DELETE is what deleting an index does, in the person's words. */
export const INDEX_DELETE =
  "It was derived, so nothing of record is lost; declare it again to rebuild it. Search through it stops until then.";

/** Indexes is the store's indexes, each row opening its page, *Delete* in its menu; *Declare an index* opens its form in the tab's place. */
function Indexes({
  store,
  mark,
  onDeclare,
}: {
  store: string;
  mark: string | undefined;
  onDeclare: () => void;
}) {
  const s = useSurface();
  const [indexes, reload] = useLoad(`store/${store}/indexes/${mark ?? ""}`, () =>
    s.bus.stores().listIndexes(store),
  );
  const [err, setErr] = useState<unknown>(undefined);
  const remove = (ix: NamedIndex) => {
    setErr(undefined);
    s.bus
      .stores()
      .deleteIndex(store, ix.name)
      .then(
        () => {
          reload();
        },
        (e: unknown) => {
          setErr(e);
        },
      );
  };
  const declare = (
    <Button size="sm" onClick={onDeclare}>
      <Plus strokeWidth={1.8} aria-hidden="true" />
      Declare an index
    </Button>
  );
  return (
    <section className="section">
      <div className="section-head">
        <h2>Indexes</h2>
        {declare}
      </div>
      {err !== undefined && <NotAllowed err={err} />}
      {indexes.state === "pending" && <Pending what="Reading the indexes" />}
      {indexes.state === "refused" && <NotAllowed err={indexes.err} what="Indexes" />}
      {indexes.state === "ready" && indexes.value.length === 0 && (
        <Empty action={declare}>
          No indexes yet. An index is a view declared on the store and derived from its history: search over
          every text of an item&apos;s current state, or of its changes.
        </Empty>
      )}
      {indexes.state === "ready" && indexes.value.length > 0 && (
        <Table
          label="Indexes"
          rows={indexes.value}
          rowKey={(ix) => ix.name}
          sort={{ key: "index", dir: "asc" }}
          columns={indexColumns}
          mark={mark}
          to={(ix) => indexAddress(s.tenant, store, ix.name)}
          menu={(ix) => [
            {
              label: "Delete",
              danger: true,
              onSelect: () => {
                remove(ix);
              },
              confirm: {
                title: `Delete index ${ix.name}?`,
                consequence: INDEX_DELETE,
              },
            },
          ]}
        />
      )}
    </section>
  );
}

const indexColumns: Column<NamedIndex>[] = [
  {
    key: "index",
    header: "Index",
    width: "minmax(0, 1fr)",
    value: (ix) => ix.name,
    cell: (ix) => <span className="cell-main mono">{ix.name}</span>,
  },
  {
    key: "kind",
    header: "Kind",
    width: "120px",
    facet: "Kind",
    value: (ix) => ix.kind,
    cell: (ix) => <Pill>{ix.kind}</Pill>,
  },
  {
    key: "reads",
    header: "Reads",
    width: "minmax(0, 1fr)",
    narrow: false,
    value: (ix) => (ix.config.source === IndexSource.history ? "the changes" : "current state"),
    cell: (ix) => (
      <span className="small muted">
        {ix.config.source === IndexSource.history ? "the changes" : "current state"}
        {ix.config.types !== undefined && ix.config.types.length > 0
          ? ` (${ix.config.types.join(", ")})`
          : ""}
      </span>
    ),
  },
];

function DeclareIndex({
  store,
  onClose,
  onDeclared,
}: {
  store: string;
  onClose: () => void;
  onDeclared: (name: string) => void;
}) {
  const s = useSurface();
  const [name, setName] = useState("");
  const [source, setSource] = useState<string>(IndexSource.state);
  const [types, setTypes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const nameOK = validName(name.trim());
  const typeList = types
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t !== "");
  const submit = () => {
    if (busy || !nameOK) {
      return;
    }
    const decl: IndexDeclaration = { kind: IndexKind.search, config: {} };
    if (source === IndexSource.history) {
      decl.config.source = IndexSource.history;
      if (typeList.length > 0) {
        decl.config.types = typeList;
      }
    }
    setErr(undefined);
    setBusy(true);
    s.bus
      .stores()
      .declareIndex(store, name.trim(), decl)
      .then(
        () => {
          setBusy(false);
          onDeclared(name.trim());
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
        },
      );
  };
  return (
    <FormPage
      title="Declare an index"
      heading="h2"
      back="Settings"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || !nameOK}>
            {busy ? "Declaring…" : "Declare"}
          </Button>
        </>
      }
    >
      <p className="muted">
        Search is the one kind so far: every text of what it reads, with no fields to map. It is built from
        the history and rebuilt whenever the store starts; losing it costs nothing.
      </p>
      {err !== undefined && <NotAllowed err={err} what="Declare" />}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="Name" hint="Lowercase letters, digits and hyphens.">
          <TextInput value={name} onChange={setName} placeholder="search" mono required autoFocus />
        </Field>
        <div className="field">
          <span className="label">Reads</span>
          <Choices
            name="source"
            value={source}
            onChange={setSource}
            options={[
              { value: IndexSource.state, label: "Current state", hint: "Each item as it stands now." },
              {
                value: IndexSource.history,
                label: "The changes",
                hint: "Every change as it was written, whatever its type — for a store whose findable text is its log.",
              },
            ]}
          />
        </div>
        {source === IndexSource.history && (
          <Field label="Only these changes" hint="Change types, comma-separated; every one when empty.">
            <TextInput value={types} onChange={setTypes} placeholder="comment.add, note.set" mono />
          </Field>
        )}
      </form>
    </FormPage>
  );
}
